import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Checkbox, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { AddIcon } from '@reapit/elements/icons/add'
import { ChevronDownIcon } from '@reapit/elements/icons/chevron-down'
import { ChevronRightIcon } from '@reapit/elements/icons/chevron-right'
import { toast } from '@reapit/elements/core/toaster'
import { TextareaControl } from '@reapit/elements/core/textarea-control'
import { useAdminEnums } from '../../../../api/enums'
import { useAdminMergeFields } from '../../../../api/merge-fields'
import { AdminFormField, AdminFormFieldPayload, ConditionSource, EnumOption } from '../../../../api/types'
import { useCreateAdminFormField, useUpdateAdminFormField } from '../../../../api/form-fields'
import { useFieldAuthoring } from '../../../../api/field-authoring'
import {
  CollapsibleToggle,
  FieldHelp,
  FieldStack,
  OptionRemoveWrap,
  OptionRowLayout,
  OptionsBlock,
  SectionHeading,
} from '../../../../components/form-details-fields/style'

// Scalar (single-control) field types.
const SCALAR_EDIT_TYPES = ['Text', 'TextArea', 'Date', 'Time', 'Currency', 'Number']
// Composite types authored with a list of options (child FormFields linked by dependencies).
const COMPOSITE_EDIT_TYPES = ['Select', 'RadioGroup', 'CheckboxGroup']
const AUTHORABLE_EDIT_TYPES = [...SCALAR_EDIT_TYPES, ...COMPOSITE_EDIT_TYPES]

// dataType shares the same enum as editType, but has no UI of its own here. For a Number/Date/Time/Currency
// field its dataType should match, so a value legitimately typed that way isn't silently overwritten by an
// edit; for every other type (Text/TextArea, and every composite - their stored value is always a plain
// string), dataType is normalised to Text, which is also what corrects a stale value left over from unrelated
// earlier experimentation (e.g. the old boolean-select toggle) that this drawer has no way to clear otherwise.
const DATA_TYPE_BY_EDIT_TYPE: Record<string, string> = {
  Number: 'Number',
  Date: 'Date',
  Time: 'Time',
  Currency: 'Currency',
}

// Hidden/data-only field: editType None (stored, not rendered to the agent — e.g. a boolean mirror).
const HIDDEN_TYPE: EnumOption = { value: 'None', label: 'Hidden (data only)' }

// Explicit "None" validation option (value matches the backend ValidationType.None member).
const NONE_VALIDATION: EnumOption = { value: 'None', label: 'None – no validation' }

// Mirrors the backend's Reapit.Services.Forms.Domain.Common.JsonObjectName struct — the known, already-used
// shared JSON object names a field's value can be grouped under. Picking one of these (or typing a new one)
// is what makes PayloadComponentFactory.BuildPayload nest the saved value the same way the agent app itself
// nests it when submitting — leaving this unset causes the two to disagree and the value silently fails to save.
const JSON_OBJECT_NAME_OPTIONS: EnumOption[] = [
  { value: 'property', label: 'Property' },
  { value: 'lease', label: 'Lease' },
  { value: 'services', label: 'Services' },
  { value: 'pets', label: 'Pets' },
  { value: 'recipients', label: 'Recipients' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'entry', label: 'Entry' },
  { value: 'notice', label: 'Notice' },
]
const CUSTOM_JSON_OBJECT_NAME: EnumOption = { value: '__custom__', label: 'Custom…' }

// The backend's EnumCatalog carries one entry per master-payload group, keyed "FieldKeysByGroup.<group>"
// (e.g. "FieldKeysByGroup.lease") - see Reapit.Services.Forms.Admin.Business.Enums.EnumCatalog. Looking one
// up by the selected Group Key gives the known field names shared across forms for that group.
const fieldKeysByGroupKey = (jsonObjectName: string): string => `FieldKeysByGroup.${jsonObjectName}`
const CUSTOM_JSON_PROPERTY_NAME: EnumOption = { value: '__custom__', label: 'Custom…' }

interface OptionRow {
  key: string
  /** Present when the option is an existing FormField (edit); absent for a new option. */
  referenceID?: string
  label: string
  value: string
  /** CheckboxGroup only - an admin-authored long-text value (e.g. a statutory reason's full wording) merged
   *  onto a PDF field when this option is selected, distinct from this option's own short `value` key. */
  pdfMergeText?: string
  /** CheckboxGroup only - whether the "PDF merge text" textarea is shown for this option. Seeded true on
   *  reopen when the option already has pdfMergeText, so existing authored text isn't hidden. */
  writeToPdf?: boolean
}

let optionKeySeq = 0
const nextOptionKey = () => `option-${(optionKeySeq += 1)}`

interface ConditionRow {
  key: string
  /** Present when this row backs an already-saved dependency (edit); absent for a newly-added row. */
  dependencyRef?: string
  /** The controlling composite field (drives which options are offered below). */
  controllingFieldRef: string
  /** The option of the controlling field that triggers this condition. */
  optionRef: string
}

let conditionKeySeq = 0
const nextConditionKey = () => `condition-${(conditionKeySeq += 1)}`

interface FieldValues {
  editType: string
  editDisplayName: string
  jsonPropertyName: string
  jsonObjectName: string
  validation: string
  defaultData: string
  editPlaceholder: string
  editDisplayDescription: string
  editWidth: string
  editOrder: string
  min: string
  max: string
  displayGroupName: string
  displayGroupDescription: string
  displayGroupIndex: string
  displayGroupSubIndex: string
  displayPrefix: string
  displaySuffix: string
  order: string
}

const fieldSchema = yup.object({
  editType: yup.string().required('Select a field type'),
  editDisplayName: yup.string().optional().default(''),
  jsonPropertyName: yup.string().trim().required('Enter a field key'),
  jsonObjectName: yup.string().trim().required('Select or enter a Group Key'),
  validation: yup.string().optional().default(''),
  defaultData: yup.string().optional().default(''),
  editPlaceholder: yup.string().optional().default(''),
  editDisplayDescription: yup.string().optional().default(''),
  editWidth: yup.string().optional().default(''),
  editOrder: yup.string().optional().default('0'),
  min: yup.string().optional().default(''),
  max: yup.string().optional().default(''),
  displayGroupName: yup.string().optional().default(''),
  displayGroupDescription: yup.string().optional().default(''),
  displayGroupIndex: yup.string().optional().default('0'),
  displayGroupSubIndex: yup.string().optional().default('0'),
  displayPrefix: yup.string().optional().default(''),
  displaySuffix: yup.string().optional().default(''),
  order: yup.string().optional().default('0'),
})

const defaultFieldPayload: AdminFormFieldPayload = {
  jsonPropertyName: null,
  jsonObjectName: null,
  jsonObjectIsArray: false,
  fieldType: 'None',
  dataType: 'Text',
  validation: 'None',
  displayGroupName: '',
  displayGroupDescription: null,
  displayGroupIndex: 0,
  displayGroupSubIndex: 0,
  displayPrefix: null,
  displaySuffix: null,
  order: 0,
  defaultData: null,
  booleanTrueData: null,
  booleanFalseData: null,
  defaultDataHasMergeField: false,
  editType: 'None',
  editWidth: null,
  editDisplayName: null,
  editDisplayDescription: null,
  editPlaceholder: null,
  editOrder: 0,
  editAllowAddSection: null,
  editAddSectionButtonText: null,
  min: null,
  max: null,
}

/** camelCase a label into a JSON field key, e.g. "Maximum Occupancy" -> "maximumOccupancy". */
const toFieldKey = (label: string): string => {
  const words = label
    .trim()
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
  if (words.length === 0) return ''
  return words
    .map((word, index) => {
      return index === 0 ? word.charAt(0).toLowerCase() + word.slice(1) : word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join('')
}

const toNumber = (value: string): number | null => {
  const parsed = Number(value)
  return value.trim() === '' || Number.isNaN(parsed) ? null : parsed
}

/** The type value shown in the drawer for a field. An existing field with editType None is a Hidden field
 * (value 'None' → matches HIDDEN_TYPE); a brand-new field has no type yet (''). */
const deriveEditTypeValue = (field: AdminFormField | null): string => {
  if (!field) return ''
  return field.editType || ''
}

interface FieldDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  sectionReferenceID: string
  /** The field being edited, or null to create a new one. */
  field: AdminFormField | null
  /** For a composite field being edited: its existing option child fields. */
  options?: AdminFormField[]
  /** Other composite fields in the section this field's visibility can depend on. */
  conditionSources?: ConditionSource[]
  /** This field's existing visibility conditions (shown when ANY match), each with the dependency row
   *  backing it (for updating/removing that specific condition). Empty = always shown. */
  currentConditions?: { dependencyRef: string; optionRef: string }[]
  /** When set, this field is a child of a Collection: it stores under the collection's array key and links
   * to the collection's Group rather than sitting at the section top level. Restricts to scalar types. */
  collectionContext?: { jsonObjectName: string; groupReferenceID: string } | null
  /** Item keys already used by a Collection in this section. A normal (non-collection-child) field must not
   * reuse one as its Group Key - the collection stores that key as a JSON array and a plain field writing a
   * property into it collides on save. */
  reservedCollectionItemKeys?: string[]
  /** Default edit order for a new field (appended to the end). */
  nextOrder: number
  onSaved: () => void
}

/**
 * Add/Edit drawer for a form field on a `None` section. Scalar fields save via the section field hooks;
 * composite fields (Select / RadioGroup / Boolean) additionally create option child fields and link each to
 * the parent via a dependency (orchestrated imperatively — see useFieldAuthoring).
 */
export const FieldDrawer: React.FC<FieldDrawerProps> = ({
  isOpen,
  onOpenChange,
  sectionReferenceID,
  field,
  options: existingOptions,
  conditionSources = [],
  currentConditions = [],
  collectionContext = null,
  reservedCollectionItemKeys = [],
  nextOrder,
  onSaved,
}) => {
  const formId = 'field-form'
  const { data: enums } = useAdminEnums()
  const { data: mergeFields } = useAdminMergeFields()
  const [createField, creating] = useCreateAdminFormField(sectionReferenceID)
  const [updateField, updating] = useUpdateAdminFormField(sectionReferenceID, field?.referenceID ?? '')
  const { createFormField, updateFormField, createDependency, updateDependency, deleteDependency } = useFieldAuthoring()
  const [submitting, setSubmitting] = useState(false)
  const isBusy = creating || updating || submitting

  const editTypeOptions = useMemo<EnumOption[]>(() => {
    const base = (enums?.FieldDataType ?? []).filter((option) =>
      (collectionContext ? SCALAR_EDIT_TYPES : AUTHORABLE_EDIT_TYPES).includes(option.value),
    )
    // Hidden (data-only) fields aren't valid inside a collection item.
    return collectionContext ? base : [...base, HIDDEN_TYPE]
  }, [enums, collectionContext])
  const validationOptions = useMemo<EnumOption[]>(() => [NONE_VALIDATION, ...(enums?.ValidationType ?? [])], [enums])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FieldValues>({
    resolver: yupResolver(fieldSchema),
    defaultValues: {
      editType: '',
      editDisplayName: '',
      jsonPropertyName: '',
      jsonObjectName: '',
      validation: 'None',
      defaultData: '',
      editPlaceholder: '',
      editDisplayDescription: '',
      editWidth: '',
      editOrder: String(nextOrder),
      min: '',
      max: '',
      displayGroupName: '',
      displayGroupDescription: '',
      displayGroupIndex: '0',
      displayGroupSubIndex: '0',
      displayPrefix: '',
      displaySuffix: '',
      order: String(nextOrder),
    },
  })

  const [editTypeOption, setEditTypeOption] = useState<EnumOption | null>(null)
  const [validationOption, setValidationOption] = useState<EnumOption>(NONE_VALIDATION)
  const [keyEdited, setKeyEdited] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [options, setOptions] = useState<OptionRow[]>([])
  /** For composites: the option value selected by default (stored as the parent field's DefaultData). */
  const [defaultOptionValue, setDefaultOptionValue] = useState('')
  /** Visibility conditions: shown when ANY row's controlling field has the row's chosen option (empty =
   *  always shown). */
  const [conditions, setConditions] = useState<ConditionRow[]>([])
  /** For composites: store the choice as a boolean (True/False values + BooleanTrue/FalseData labels). */
  const [isBooleanField, setIsBooleanField] = useState(false)
  /** Scalar fields: whether the default value is a merge token (sets defaultDataHasMergeField). */
  const [defaultIsMergeField, setDefaultIsMergeField] = useState(false)
  /** Group Key: whether the admin is typing their own value rather than picking a known one. */
  const [jsonObjectNameIsCustom, setJsonObjectNameIsCustom] = useState(false)
  /** Field Key: whether the admin is typing their own value rather than picking a known one for the group. */
  const [jsonPropertyNameIsCustom, setJsonPropertyNameIsCustom] = useState(false)

  const jsonObjectNameSelectOptions = useMemo<EnumOption[]>(
    () => [...JSON_OBJECT_NAME_OPTIONS, CUSTOM_JSON_OBJECT_NAME],
    [],
  )
  const jsonObjectNameValue = watch('jsonObjectName')
  const jsonObjectNameOption = jsonObjectNameIsCustom
    ? CUSTOM_JSON_OBJECT_NAME
    : (JSON_OBJECT_NAME_OPTIONS.find((option) => option.value === jsonObjectNameValue) ?? null)

  // The known field keys for the currently-selected group (empty once the Group Key itself is custom/unset -
  // there's nothing to look up, so the Field Key falls straight to its own custom free-text entry).
  const fieldKeyOptionsForGroup = useMemo<EnumOption[]>(() => {
    if (jsonObjectNameIsCustom || !jsonObjectNameValue) return []
    return enums?.[fieldKeysByGroupKey(jsonObjectNameValue)] ?? []
  }, [enums, jsonObjectNameIsCustom, jsonObjectNameValue])
  const jsonPropertyNameSelectOptions = useMemo<EnumOption[]>(
    () => [...fieldKeyOptionsForGroup, CUSTOM_JSON_PROPERTY_NAME],
    [fieldKeyOptionsForGroup],
  )
  const jsonPropertyNameValue = watch('jsonPropertyName')
  const jsonPropertyNameOption = jsonPropertyNameIsCustom
    ? CUSTOM_JSON_PROPERTY_NAME
    : (fieldKeyOptionsForGroup.find((option) => option.value === jsonPropertyNameValue) ?? null)

  const mergeFieldOptions = useMemo<EnumOption[]>(
    () => (mergeFields ?? []).map((field) => ({ value: field.token, label: `${field.category} · ${field.name}` })),
    [mergeFields],
  )

  const selectedType = editTypeOption?.value ?? ''
  const isComposite = COMPOSITE_EDIT_TYPES.includes(selectedType)

  const NO_DEFAULT: EnumOption = { value: '', label: 'No default' }
  const defaultSelectionOptions = useMemo<EnumOption[]>(
    () => [
      NO_DEFAULT,
      ...options
        .filter((option) => option.value)
        .map((option) => ({ value: option.value, label: option.label || option.value })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options],
  )
  const defaultSelectionValue =
    defaultSelectionOptions.find((option) => option.value === defaultOptionValue) ?? NO_DEFAULT

  const controllingOptions = useMemo<EnumOption[]>(
    () => conditionSources.map((source) => ({ value: source.fieldReferenceID, label: source.label })),
    [conditionSources],
  )
  const optionChoicesFor = (controllingFieldRef: string): EnumOption[] => {
    const source = conditionSources.find((entry) => entry.fieldReferenceID === controllingFieldRef)
    return (source?.options ?? []).map((option) => ({ value: option.referenceID, label: option.label }))
  }

  useEffect(() => {
    if (!isOpen) return
    setKeyEdited(Boolean(field))
    setShowAdvanced(false)
    setDefaultOptionValue(field?.defaultData ?? '')
    setIsBooleanField(Boolean(field?.booleanTrueData || field?.booleanFalseData))
    setDefaultIsMergeField(Boolean(field?.defaultDataHasMergeField))
    // A collection child's Group Key is forced to the collection's own key at submit time (see below) —
    // pre-fill it here too, otherwise this required field defaults to blank and blocks saving, or shows a
    // value the admin picks that gets silently discarded anyway.
    const effectiveJsonObjectName = collectionContext?.jsonObjectName ?? field?.jsonObjectName ?? ''
    const jsonObjectNameCustom =
      Boolean(effectiveJsonObjectName) &&
      !JSON_OBJECT_NAME_OPTIONS.some((option) => option.value === effectiveJsonObjectName)
    setJsonObjectNameIsCustom(jsonObjectNameCustom)

    // Same custom-detection as Group Key above, but scoped to the known field keys for that group - a
    // custom/unset Group Key has no group-specific list to check against, so the Field Key is custom too.
    const effectiveJsonPropertyName = field?.jsonPropertyName ?? ''
    const knownFieldKeysForGroup = jsonObjectNameCustom
      ? []
      : (enums?.[fieldKeysByGroupKey(effectiveJsonObjectName)] ?? [])
    setJsonPropertyNameIsCustom(
      Boolean(effectiveJsonPropertyName) &&
        !knownFieldKeysForGroup.some((option) => option.value === effectiveJsonPropertyName),
    )

    setConditions(
      currentConditions.map((condition) => {
        const source = conditionSources.find((entry) =>
          entry.options.some((option) => option.referenceID === condition.optionRef),
        )
        return {
          key: nextConditionKey(),
          dependencyRef: condition.dependencyRef,
          controllingFieldRef: source?.fieldReferenceID ?? '',
          optionRef: condition.optionRef,
        }
      }),
    )

    const derived = deriveEditTypeValue(field)
    const composite = COMPOSITE_EDIT_TYPES.includes(derived)
    // A Checkbox child's identifying "value" lives in its own jsonPropertyName, not defaultData (see
    // submitComposite's optionPayload for why) - read back from whichever one this type's children actually
    // use, or reopening an existing CheckboxGroup would show blank "value" inputs for every option.
    const isExistingCheckboxGroup = derived === 'CheckboxGroup'
    setOptions(
      composite
        ? [...(existingOptions ?? [])]
            .sort((a, b) => a.editOrder - b.editOrder)
            .map((option) => ({
              key: nextOptionKey(),
              referenceID: option.referenceID,
              label: option.editDisplayName ?? '',
              value: (isExistingCheckboxGroup ? option.jsonPropertyName : option.defaultData) ?? '',
              // For a Checkbox child, defaultData is free for this long-text use (its identifying value is
              // jsonPropertyName instead - see above), unlike a RadioButton/SelectOption where defaultData
              // already IS the identifying value.
              pdfMergeText: isExistingCheckboxGroup ? (option.defaultData ?? '') : '',
              writeToPdf: isExistingCheckboxGroup ? Boolean(option.defaultData) : false,
            }))
        : [],
    )

    reset({
      editType: derived,
      editDisplayName: field?.editDisplayName ?? '',
      jsonPropertyName: field?.jsonPropertyName ?? '',
      jsonObjectName: effectiveJsonObjectName,
      validation: field?.validation || 'None',
      defaultData: field?.defaultData ?? '',
      editPlaceholder: field?.editPlaceholder ?? '',
      editDisplayDescription: field?.editDisplayDescription ?? '',
      editWidth: field?.editWidth != null ? String(field.editWidth) : '',
      editOrder: field?.editOrder != null ? String(field.editOrder) : String(nextOrder),
      min: field?.min != null ? String(field.min) : '',
      max: field?.max != null ? String(field.max) : '',
      displayGroupName: field?.displayGroupName ?? '',
      displayGroupDescription: field?.displayGroupDescription ?? '',
      displayGroupIndex: field?.displayGroupIndex != null ? String(field.displayGroupIndex) : '0',
      displayGroupSubIndex: field?.displayGroupSubIndex != null ? String(field.displayGroupSubIndex) : '0',
      displayPrefix: field?.displayPrefix ?? '',
      displaySuffix: field?.displaySuffix ?? '',
      order: field?.order != null ? String(field.order) : String(nextOrder),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, field, collectionContext])

  // useAdminEnums() can still be loading when the drawer first opens, in which case the init effect above
  // computed jsonPropertyNameIsCustom against an empty catalog - a genuinely catalogued Field Key would
  // wrongly stay stuck in custom mode for the rest of this drawer session. Once per open, reconcile ONLY
  // that custom/dropdown display flag against the real catalog as soon as it arrives - never the stored
  // jsonPropertyName value itself, and never once the admin has started changing Group/Field Key themselves
  // (their in-progress choice wins over a late-arriving catalog).
  const reconciledFieldKeyModeRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen || !field || !enums) return
    if (reconciledFieldKeyModeRef.current === field.referenceID) return
    reconciledFieldKeyModeRef.current = field.referenceID

    const effectiveJsonObjectName = collectionContext?.jsonObjectName ?? field.jsonObjectName ?? ''
    const unchangedSinceOpen =
      jsonObjectNameValue === effectiveJsonObjectName && jsonPropertyNameValue === (field.jsonPropertyName ?? '')
    if (!unchangedSinceOpen || jsonObjectNameIsCustom || !field.jsonPropertyName) return

    const knownFieldKeysForGroup = enums[fieldKeysByGroupKey(effectiveJsonObjectName)] ?? []
    setJsonPropertyNameIsCustom(!knownFieldKeysForGroup.some((option) => option.value === field.jsonPropertyName))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, field, enums])

  useEffect(() => {
    if (!isOpen) return
    const derived = deriveEditTypeValue(field)
    const validation = field?.validation || 'None'
    setEditTypeOption(derived ? (editTypeOptions.find((option) => option.value === derived) ?? null) : null)
    setValidationOption(validationOptions.find((option) => option.value === validation) ?? NONE_VALIDATION)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, field, editTypeOptions, validationOptions])

  // Auto-suggest the field key from the display name until the user edits the key themselves (create only,
  // and only while picking a custom key - selecting a known field key from the dropdown is an exact choice,
  // not something a display-name edit should silently overwrite).
  const displayNameValue = watch('editDisplayName')
  useEffect(() => {
    if (!isOpen || field || keyEdited || !jsonPropertyNameIsCustom) return
    // shouldValidate is required here, not optional - selecting "Custom..." forces an immediate validation
    // against an empty jsonPropertyName (see the Field Key select's onChange below), which sets an error.
    // setValue() alone never clears a previously-set error (that only happens via shouldValidate or a
    // register()-driven onChange/reValidateMode), so without this the error stays displayed - and the same
    // stale value lingers internally too, since nothing has told the form the field actually changed - even
    // once this auto-fill gives it a genuinely valid value, leaving Save silently doing nothing.
    setValue('jsonPropertyName', toFieldKey(displayNameValue || ''), { shouldValidate: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayNameValue, jsonPropertyNameIsCustom])

  const handleTypeChange = (option: EnumOption | null) => {
    const next = option?.value
    setEditTypeOption(option)
    setValue('editType', next ?? '', { shouldValidate: true })

    // Seed two blank option rows the first time a composite type is chosen.
    if (COMPOSITE_EDIT_TYPES.includes(next ?? '') && options.length === 0) {
      setOptions([
        { key: nextOptionKey(), label: '', value: '' },
        { key: nextOptionKey(), label: '', value: '' },
      ])
    }

    // "Store as a Yes/No boolean" and a single-select default have no meaning for a CheckboxGroup (its own
    // value is a multi-select array, not a single true/false) - clear both if the admin had them set before
    // switching type, otherwise the default selector hides but submitComposite still sends the stale
    // defaultOptionValue as the parent's defaultData.
    if (next === 'CheckboxGroup') {
      setIsBooleanField(false)
      setDefaultOptionValue('')
      // Min/Max inputs are hidden for a composite type (isComposite), but their form values survive the
      // switch - and submitComposite now saves them as CheckboxGroup selection-count bounds. A value left
      // over from a scalar type (e.g. Min = 5 entered while this was a Number field) would otherwise become
      // an impossible-to-satisfy minItems on a group with fewer options than that.
      setValue('min', '')
      setValue('max', '')
    }
  }

  // Toggling boolean locks the choices to exactly two rows with True/False values (labels stay editable).
  const handleBooleanToggle = (checked: boolean) => {
    setIsBooleanField(checked)
    if (checked) {
      setOptions((prev) => {
        const [first, second] = prev
        return [
          {
            key: first?.key ?? nextOptionKey(),
            referenceID: first?.referenceID,
            label: first?.label || 'Yes',
            value: 'True',
          },
          {
            key: second?.key ?? nextOptionKey(),
            referenceID: second?.referenceID,
            label: second?.label || 'No',
            value: 'False',
          },
        ]
      })
    }
  }

  const addOption = () => setOptions((prev) => [...prev, { key: nextOptionKey(), label: '', value: '' }])
  const updateOption = (index: number, key: 'label' | 'value' | 'pdfMergeText', value: string) =>
    setOptions((prev) =>
      prev.map((option, i) => {
        return i === index ? { ...option, [key]: value } : option
      }),
    )
  const removeOption = (index: number) => setOptions((prev) => prev.filter((_, i) => i !== index))

  const addCondition = () =>
    setConditions((prev) => [...prev, { key: nextConditionKey(), controllingFieldRef: '', optionRef: '' }])
  const updateConditionControllingField = (index: number, controllingFieldRef: string) =>
    setConditions((prev) =>
      prev.map((condition, i) => {
        return i === index ? { ...condition, controllingFieldRef, optionRef: '' } : condition
      }),
    )
  const updateConditionOption = (index: number, optionRef: string) =>
    setConditions((prev) =>
      prev.map((condition, i) => {
        return i === index ? { ...condition, optionRef } : condition
      }),
    )
  const removeCondition = (index: number) => setConditions((prev) => prev.filter((_, i) => i !== index))

  const handleClose = () => onOpenChange(false)

  const buildBasePayload = (values: FieldValues): AdminFormFieldPayload => {
    const base: AdminFormFieldPayload = field
      ? (() => {
          const { referenceID, ...rest } = field
          void referenceID
          return rest
        })()
      : { ...defaultFieldPayload }

    return {
      ...base,
      dataType: DATA_TYPE_BY_EDIT_TYPE[selectedType] ?? 'Text',
      editDisplayName: values.editDisplayName || null,
      jsonPropertyName: values.jsonPropertyName || null,
      jsonObjectName: values.jsonObjectName || null,
      editPlaceholder: values.editPlaceholder || null,
      editDisplayDescription: values.editDisplayDescription || null,
      editWidth: toNumber(values.editWidth),
      editOrder: toNumber(values.editOrder) ?? 0,
      displayGroupName: values.displayGroupName || '',
      displayGroupDescription: values.displayGroupDescription || null,
      displayGroupIndex: toNumber(values.displayGroupIndex) ?? 0,
      displayGroupSubIndex: toNumber(values.displayGroupSubIndex) ?? 0,
      displayPrefix: values.displayPrefix || null,
      displaySuffix: values.displaySuffix || null,
      order: toNumber(values.order) ?? 0,
    }
  }

  // Reconcile this field's visibility conditions against what's currently saved: create rows that are new,
  // update rows whose target option changed, and delete rows that were removed from the list. Callers must
  // validate every row is complete (see hasIncompleteCondition) before calling this - an incomplete row here
  // would either delete a still-relevant saved condition or, if instead treated as "kept", leave a stale
  // server-side condition active that no longer matches what the UI showed when the drawer was saved.
  const applyConditions = async (fieldReferenceID: string) => {
    const keptDependencyRefs = new Set<string>()
    // Two rows targeting the same option would otherwise call createDependency/updateDependency twice for the
    // same target - de-duplicate by optionRef so only the first row with a given target is actually applied.
    const seenOptionRefs = new Set<string>()

    for (const condition of conditions) {
      if (!condition.optionRef || seenOptionRefs.has(condition.optionRef)) continue
      seenOptionRefs.add(condition.optionRef)
      if (condition.dependencyRef) keptDependencyRefs.add(condition.dependencyRef)

      if (condition.dependencyRef) {
        const original = currentConditions.find((entry) => entry.dependencyRef === condition.dependencyRef)
        if (original && original.optionRef !== condition.optionRef) {
          await updateDependency(fieldReferenceID, condition.dependencyRef, {
            dependencyFieldReferenceID: condition.optionRef,
            conditionalValue: null,
          })
        }
      } else {
        await createDependency(fieldReferenceID, {
          dependencyFieldReferenceID: condition.optionRef,
          conditionalValue: null,
        })
      }
    }

    const removed = currentConditions.filter((entry) => !keptDependencyRefs.has(entry.dependencyRef))
    for (const entry of removed) {
      await deleteDependency(fieldReferenceID, entry.dependencyRef)
    }
  }

  const submitScalar: SubmitHandler<FieldValues> = async (values) => {
    const payload: AdminFormFieldPayload = {
      ...buildBasePayload(values),
      editType: values.editType,
      validation: values.validation || 'None',
      defaultData: values.defaultData || null,
      defaultDataHasMergeField: defaultIsMergeField,
      min: toNumber(values.min),
      max: toNumber(values.max),
    }

    // A collection child stores under the collection's array key and links to its Group.
    if (collectionContext) {
      payload.jsonObjectName = collectionContext.jsonObjectName
      payload.jsonObjectIsArray = true
      setSubmitting(true)
      try {
        if (field) {
          await updateFormField(sectionReferenceID, field.referenceID, payload)
        } else {
          const created = await createFormField(sectionReferenceID, payload)
          await createDependency(created.referenceID, {
            dependencyFieldReferenceID: collectionContext.groupReferenceID,
            conditionalValue: null,
          })
        }
        toast.success(field ? 'Field updated' : 'Field created')
        onSaved()
        onOpenChange(false)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to save the field')
      } finally {
        setSubmitting(false)
      }
      return
    }

    try {
      const saved = field ? await updateField(payload) : await createField(payload)
      const fieldReferenceID = field ? field.referenceID : (saved as AdminFormField).referenceID
      try {
        await applyConditions(fieldReferenceID)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Saved the field, but failed to set its visibility.')
      }
      onSaved()
      onOpenChange(false)
    } catch {
      // Field-save errors surfaced via the hooks' toasts.
    }
  }

  const submitComposite = async (values: FieldValues) => {
    const cleanOptions = options.map((option) => ({
      ...option,
      label: option.label.trim(),
      value: option.value.trim(),
    }))

    if (cleanOptions.length === 0 || cleanOptions.some((option) => !option.label || !option.value)) {
      toast.error('Add at least one option, each with a label and a stored value.')
      return
    }

    const isCheckboxGroup = selectedType === 'CheckboxGroup'

    const parentPayload: AdminFormFieldPayload = {
      ...buildBasePayload(values),
      editType: selectedType,
      validation: values.validation || 'None',
      defaultData: defaultOptionValue || null,
      booleanTrueData: isBooleanField ? (cleanOptions[0]?.label ?? null) : null,
      booleanFalseData: isBooleanField ? (cleanOptions[1]?.label ?? null) : null,
      // Select/RadioGroup have no min/max concept (single-select), so those stay null. A CheckboxGroup's
      // min/max ARE meaningful (getCheckboxGroupDefinition enforces them as minItems/maxItems) but the drawer
      // currently has no UI to edit them for a composite type - values.min/max still carry the existing
      // field's stored values through from the reset() seed above, so preserve them here rather than wiping
      // them to null on every save.
      min: isCheckboxGroup ? toNumber(values.min) : null,
      max: isCheckboxGroup ? toNumber(values.max) : null,
    }

    // Defensive: composite types aren't currently offered inside a collection (editTypeOptions restricts a
    // collection child to scalar types), but if that ever changes, a composite child must store under the
    // collection's array key just like submitScalar does - otherwise it lands in the backend's non-array
    // write branch and collides with the collection array. Keep the array-key contract consistent here too.
    if (collectionContext) {
      parentPayload.jsonObjectName = collectionContext.jsonObjectName
      parentPayload.jsonObjectIsArray = true
    }

    // An existing option's "value" is seeded straight from its stored jsonPropertyName (see the reset() init
    // above), which is already a valid key - re-normalizing it through toFieldKey on every save can silently
    // rename it (e.g. "inspection_required" -> "inspectionRequired"), orphaning any already-submitted
    // document's selection array that still references the original key. Only re-derive the key when the
    // option is new or its value has actually been edited since the drawer opened.
    const resolveCheckboxKey = (option: (typeof cleanOptions)[number]): string => {
      const original = existingOptions?.find((entry) => entry.referenceID === option.referenceID)
      if (original && (original.jsonPropertyName ?? '') === option.value) return original.jsonPropertyName ?? ''
      return toFieldKey(option.value)
    }

    if (isCheckboxGroup) {
      // Checkbox selection is array-membership on each option's own resolved jsonPropertyName (see the
      // comment below on optionPayload.jsonPropertyName) - an empty or colliding key makes an option
      // unselectable or indistinguishable from another, so reject before any writes happen.
      const checkboxKeys = cleanOptions.map(resolveCheckboxKey)
      if (checkboxKeys.some((key) => !key) || new Set(checkboxKeys).size !== checkboxKeys.length) {
        toast.error('Checkbox values must produce non-empty, unique field keys.')
        return
      }
    }
    const optionEditType = selectedType === 'Select' ? 'SelectOption' : isCheckboxGroup ? 'Checkbox' : 'RadioButton'

    setSubmitting(true)
    try {
      const parent = field
        ? await updateFormField(sectionReferenceID, field.referenceID, parentPayload)
        : await createFormField(sectionReferenceID, parentPayload)

      for (let index = 0; index < cleanOptions.length; index += 1) {
        const option = cleanOptions[index]
        const optionPayload: AdminFormFieldPayload = {
          ...defaultFieldPayload,
          editType: optionEditType,
          editDisplayName: option.label,
          // Unlike a RadioButton/SelectOption (which has no jsonPropertyName of its own and relies on
          // DefaultData as its identifying value - the RadioGroup/Select's own stored value IS that option's
          // value), a Checkbox child needs its own real jsonPropertyName: that's the identifier the backend
          // stores in the CheckboxGroup's selected-array when ticked (see
          // ReviewCardEditModelBuilder.BuildCheckboxGroupFieldModel / UpdateReviewCardEditCommandHandler's
          // TryFindFieldValue, which both compare array items against a Checkbox's own JsonPropertyName).
          jsonPropertyName: isCheckboxGroup ? resolveCheckboxKey(option) : null,
          // A Checkbox's own defaultData is otherwise unused (its identifying value is jsonPropertyName
          // above instead), so it's free to carry this optional admin-authored long-text value - trimmed to
          // null rather than an empty string, matching the "leave as None" convention the PDF mapping drawer
          // itself uses for an unset DefaultData.
          defaultData: isCheckboxGroup ? option.pdfMergeText?.trim() || null : option.value,
          editOrder: index,
          order: index,
        }

        if (option.referenceID) {
          await updateFormField(sectionReferenceID, option.referenceID, optionPayload)
        } else {
          const created = await createFormField(sectionReferenceID, optionPayload)
          await createDependency(created.referenceID, {
            dependencyFieldReferenceID: parent.referenceID,
            conditionalValue: null,
          })
        }
      }

      await applyConditions(parent.referenceID)

      toast.success(field ? 'Field updated' : 'Field created')
      onSaved()
      onOpenChange(false)
    } catch (error) {
      toast.error(
        `${error instanceof Error ? error.message : 'Failed to save the field'} Some changes may already be applied. Please reopen and check before retrying.`,
      )
      // Options and applyConditions above run several create/update/delete calls sequentially - if one of
      // them (or an earlier step) failed partway through, some may have already succeeded against the server
      // while this drawer's own options/conditions/currentConditions still describe the pre-submit state.
      // Retrying from here would re-run the same diff against that now-stale snapshot, re-creating
      // already-created rows or re-deleting already-removed ones. Refresh the canvas and close the drawer so
      // the ONLY way to retry is reopening it, which reloads the real current state instead of continuing to
      // trust this stale one.
      onSaved()
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const onSubmit: SubmitHandler<FieldValues> = async (values) => {
    // A normal (non-collection-child) field must not reuse a Collection's item key as its Group Key: the
    // collection stores that key as a JSON array while this field would try to write a property into it,
    // which collides and fails the save server-side. Collection children (collectionContext set) legitimately
    // share the collection's key and are exempt.
    if (!collectionContext && reservedCollectionItemKeys.includes(values.jsonObjectName.trim())) {
      toast.error(
        `The Group Key "${values.jsonObjectName.trim()}" is already used by a collection in this section. Choose a different Group Key.`,
      )
      return
    }

    // A condition row missing either half of "show only when X is set to Y" can't be resolved into a
    // dependency at all - block the save so the user fixes or removes it, rather than silently either
    // deleting the saved condition it used to represent or leaving a stale one active server-side.
    if (conditions.some((condition) => !condition.controllingFieldRef || !condition.optionRef)) {
      toast.error('Complete or remove every visibility condition before saving.')
      return
    }

    if (isComposite) {
      await submitComposite(values)
      return
    }
    await submitScalar(values)
  }

  const keyRegister = register('jsonPropertyName')
  const groupKeyRegister = register('jsonObjectName')

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer isOpen={isOpen} title={field ? 'Edit Field' : 'Add Field'} onClose={handleClose} footerItems={footer}>
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
          <SectionHeading>Field</SectionHeading>
          <div>
            <Select
              label="Field Type *"
              options={editTypeOptions}
              value={editTypeOption}
              disabled={Boolean(field)}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={handleTypeChange}
              errorMessage={errors.editType?.message}
            />
            <FieldHelp>
              {field
                ? "A field's type is fixed once created. To change it, remove this field and add a new one."
                : 'The input control the agent uses. Dropdown, Radio Group and Yes / No fields let you define the choices below.'}
            </FieldHelp>
          </div>
          <InputGroup
            label="Display Name"
            labelInformationTooltip="The label shown above this field in the form."
            placeholder="e.g. Tenancy Type"
            {...register('editDisplayName')}
            errorMessage={errors.editDisplayName?.message}
          />

          <div>
            <Select
              label="Group Key *"
              options={jsonObjectNameSelectOptions}
              value={jsonObjectNameOption}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                const isCustom = option?.value === CUSTOM_JSON_OBJECT_NAME.value
                setJsonObjectNameIsCustom(isCustom)
                setValue('jsonObjectName', isCustom ? '' : (option?.value ?? ''))
                // The known field keys belong to a specific group - once that group changes, the previously
                // selected/typed Field Key almost certainly doesn't apply to the new one, so clear it rather
                // than silently keep a value picked for a different group.
                setJsonPropertyNameIsCustom(false)
                setKeyEdited(false)
                setValue('jsonPropertyName', '')
              }}
              disabled={Boolean(collectionContext)}
              errorMessage={jsonObjectNameIsCustom ? undefined : errors.jsonObjectName?.message}
            />
            <FieldHelp>
              {collectionContext ? (
                'Inherited from the collection this field belongs to — not editable here.'
              ) : (
                <>
                  The shared object this field&apos;s value is saved under (e.g. every Lease field saves under{' '}
                  <strong>lease</strong>). This has to match what the agent app itself uses to save the value — pick a
                  known one where it fits, or enter a new one if none of these are right.
                </>
              )}
            </FieldHelp>
            {jsonObjectNameIsCustom && !collectionContext && (
              <InputGroup
                label="Custom Group Key"
                placeholder="e.g. leaseExtension"
                {...groupKeyRegister}
                onChange={(event) => {
                  // The Group Key select's onChange resets Field Key when switching INTO custom mode, but
                  // further edits to the custom text itself (correcting a typo, trying a different name)
                  // only ran through register's own onChange - the Field Key reset never fired again, so a
                  // key picked/typed for an earlier custom group text could be saved under a new one.
                  setJsonPropertyNameIsCustom(false)
                  setKeyEdited(false)
                  setValue('jsonPropertyName', '')
                  groupKeyRegister.onChange(event)
                }}
                errorMessage={errors.jsonObjectName?.message}
              />
            )}
          </div>

          <div>
            <Select
              label="Field Key *"
              options={jsonPropertyNameSelectOptions}
              value={jsonPropertyNameOption}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                const isCustom = option?.value === CUSTOM_JSON_PROPERTY_NAME.value
                setJsonPropertyNameIsCustom(isCustom)
                // Picking a known key is an explicit choice (keyEdited true, so the auto-suggest effect
                // never overwrites it); picking Custom... should still let that effect seed the box from the
                // Display Name, until the custom input's own onChange marks it edited once the user types.
                setKeyEdited(!isCustom)
                setValue('jsonPropertyName', isCustom ? '' : (option?.value ?? ''), { shouldValidate: true })
              }}
              errorMessage={jsonPropertyNameIsCustom ? undefined : errors.jsonPropertyName?.message}
            />
            <FieldHelp>
              {fieldKeyOptionsForGroup.length > 0
                ? 'The known field keys already used for this Group Key — pick one where it fits, or choose Custom… to enter a new one.'
                : 'The unique key this field’s value is stored under. No known field keys for this Group Key yet, so enter your own.'}
            </FieldHelp>
            {jsonPropertyNameIsCustom && (
              <InputGroup
                label="Custom Field Key"
                placeholder="e.g. tenancyType"
                {...keyRegister}
                onChange={(event) => {
                  setKeyEdited(true)
                  keyRegister.onChange(event)
                }}
                errorMessage={errors.jsonPropertyName?.message}
              />
            )}
          </div>

          {isComposite && (
            <>
              <SectionHeading>Options</SectionHeading>
              {selectedType !== 'CheckboxGroup' && (
                <>
                  <Checkbox
                    label="Store as a Yes/No boolean"
                    supplementaryInfo="Locks the choices to two options stored as True / False (edit the labels below). Pair with Required Boolean validation."
                    checked={isBooleanField}
                    onChange={(event) => handleBooleanToggle((event.target as HTMLInputElement).checked)}
                  />
                  <FieldHelp>
                    {isBooleanField
                      ? 'The two choices shown to the agent. Edit the labels; the stored values stay True / False.'
                      : 'The choices shown to the agent. Label is what they see; value is what gets stored.'}
                  </FieldHelp>
                </>
              )}
              {selectedType === 'CheckboxGroup' && (
                <FieldHelp>
                  The checkboxes shown to the agent. Label is what they see; value becomes that checkbox&apos;s field
                  key, so keep it short and unique (e.g. &quot;inspectProperty&quot;).
                </FieldHelp>
              )}
              <OptionsBlock>
                {options.map((option, index) => (
                  <React.Fragment key={option.key}>
                    <OptionRowLayout>
                      <InputGroup
                        placeholder="Label (e.g. Fixed Term)"
                        value={option.label}
                        onChange={(event) => updateOption(index, 'label', (event.target as HTMLInputElement).value)}
                      />
                      <InputGroup
                        placeholder={
                          selectedType === 'CheckboxGroup' ? 'Value (e.g. inspectProperty)' : 'Value (e.g. fixed)'
                        }
                        value={option.value}
                        disabled={isBooleanField}
                        onChange={(event) => updateOption(index, 'value', (event.target as HTMLInputElement).value)}
                      />
                      {!isBooleanField && !option.referenceID && (
                        <OptionRemoveWrap>
                          <Button
                            variant="tertiary"
                            useLinkStyle
                            size="small"
                            type="button"
                            onClick={() => removeOption(index)}
                          >
                            Remove
                          </Button>
                        </OptionRemoveWrap>
                      )}
                    </OptionRowLayout>
                    {selectedType === 'CheckboxGroup' && (
                      <>
                        <Checkbox
                          label="Write to PDF"
                          checked={option.writeToPdf ?? false}
                          onChange={(event) => {
                            const checked = (event.target as HTMLInputElement).checked
                            setOptions((prev) =>
                              prev.map((prevOption, i) => {
                                if (i !== index) return prevOption
                                return {
                                  ...prevOption,
                                  writeToPdf: checked,
                                  pdfMergeText: checked ? prevOption.pdfMergeText : '',
                                }
                              }),
                            )
                          }}
                        />
                        {option.writeToPdf && (
                          <TextareaControl
                            label="PDF merge text"
                            helpText="Text written to the PDF when selected."
                            fieldSizing="fixed"
                            rows={2}
                            value={option.pdfMergeText ?? ''}
                            onChange={(event) => updateOption(index, 'pdfMergeText', event.target.value)}
                          />
                        )}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </OptionsBlock>
              {!isBooleanField && (
                <div>
                  <Button variant="secondary" size="small" iconLeft={<AddIcon />} type="button" onClick={addOption}>
                    Add option
                  </Button>
                </div>
              )}
              {selectedType !== 'CheckboxGroup' && (
                <div>
                  <Select
                    label="Default selection"
                    options={defaultSelectionOptions}
                    value={defaultSelectionValue}
                    getOptionLabel={(option: EnumOption) => option.label}
                    getOptionValue={(option: EnumOption) => option.value}
                    onChange={(option: EnumOption | null) => setDefaultOptionValue(option?.value ?? '')}
                  />
                  <FieldHelp>Optional — the option selected by default when the form loads.</FieldHelp>
                </div>
              )}
            </>
          )}

          <SectionHeading>Input</SectionHeading>
          <div>
            <Select
              label="Validation"
              options={validationOptions}
              value={validationOption}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                const next = option ?? NONE_VALIDATION
                setValidationOption(next)
                setValue('validation', next.value)
              }}
              errorMessage={errors.validation?.message}
            />
            <FieldHelp>Rules applied to the entered value (e.g. required, a valid date or number).</FieldHelp>
          </div>
          {!isComposite && (
            <>
              <Checkbox
                label="Default is a merge field"
                supplementaryInfo="Pre-fill from a merge token (e.g. today's date, agency suburb) instead of static text."
                checked={defaultIsMergeField}
                onChange={(event) => {
                  const checked = (event.target as HTMLInputElement).checked
                  setDefaultIsMergeField(checked)
                  setValue('defaultData', '')
                }}
              />
              {defaultIsMergeField ? (
                <Select
                  label="Merge field"
                  options={mergeFieldOptions}
                  value={mergeFieldOptions.find((option) => option.value === watch('defaultData')) ?? null}
                  getOptionLabel={(option: EnumOption) => option.label}
                  getOptionValue={(option: EnumOption) => option.value}
                  onChange={(option: EnumOption | null) => setValue('defaultData', option?.value ?? '')}
                />
              ) : (
                <InputGroup
                  label="Default Value"
                  labelInformationTooltip="Pre-fills the field with static text."
                  {...register('defaultData')}
                  errorMessage={errors.defaultData?.message}
                />
              )}
              <InputGroup
                label="Placeholder"
                labelInformationTooltip="Grey hint text shown inside the empty input."
                {...register('editPlaceholder')}
                errorMessage={errors.editPlaceholder?.message}
              />
              <InputGroup
                label="Min"
                inputMode="numeric"
                labelInformationTooltip="Numbers: smallest allowed value. Text: minimum length."
                {...register('min')}
                errorMessage={errors.min?.message}
              />
              <InputGroup
                label="Max"
                inputMode="numeric"
                labelInformationTooltip="Numbers: largest allowed value. Text: maximum length."
                {...register('max')}
                errorMessage={errors.max?.message}
              />
            </>
          )}
          <InputGroup
            label="Edit Width (%)"
            inputMode="numeric"
            labelInformationTooltip="How wide the field is in its row. 100 = full width."
            {...register('editWidth')}
            errorMessage={errors.editWidth?.message}
          />
          <InputGroup
            label="Edit Order"
            inputMode="numeric"
            labelInformationTooltip="Position of this field within the section's form."
            {...register('editOrder')}
            errorMessage={errors.editOrder?.message}
          />

          {conditionSources.length > 0 && (
            <>
              <SectionHeading>Visibility</SectionHeading>
              <FieldHelp>
                {conditions.length > 0
                  ? 'Shown only when ANY of these conditions match. Add another to show it for more than one trigger (e.g. both "Fixed" and "Periodic").'
                  : 'Optionally show this field only when another field has a specific value.'}
              </FieldHelp>
              <OptionsBlock>
                {conditions.map((condition, index) => {
                  const controllingValue =
                    controllingOptions.find((option) => option.value === condition.controllingFieldRef) ?? null
                  const optionChoices = optionChoicesFor(condition.controllingFieldRef)
                  const optionChoiceValue = optionChoices.find((option) => option.value === condition.optionRef) ?? null
                  return (
                    <OptionRowLayout key={condition.key}>
                      <Select
                        label="Show only when"
                        options={controllingOptions}
                        value={controllingValue}
                        getOptionLabel={(option: EnumOption) => option.label}
                        getOptionValue={(option: EnumOption) => option.value}
                        onChange={(option: EnumOption | null) =>
                          updateConditionControllingField(index, option?.value ?? '')
                        }
                      />
                      <Select
                        label="is set to"
                        options={optionChoices}
                        value={optionChoiceValue}
                        disabled={!condition.controllingFieldRef}
                        getOptionLabel={(option: EnumOption) => option.label}
                        getOptionValue={(option: EnumOption) => option.value}
                        onChange={(option: EnumOption | null) => updateConditionOption(index, option?.value ?? '')}
                      />
                      <OptionRemoveWrap>
                        <Button
                          variant="tertiary"
                          useLinkStyle
                          size="small"
                          type="button"
                          onClick={() => removeCondition(index)}
                        >
                          Remove
                        </Button>
                      </OptionRemoveWrap>
                    </OptionRowLayout>
                  )
                })}
              </OptionsBlock>
              <div>
                <Button variant="secondary" size="small" iconLeft={<AddIcon />} type="button" onClick={addCondition}>
                  Add condition
                </Button>
              </div>
            </>
          )}

          <CollapsibleToggle
            variant="tertiary"
            useLinkStyle
            type="button"
            onClick={() => setShowAdvanced((value) => !value)}
          >
            {showAdvanced ? <ChevronDownIcon /> : <ChevronRightIcon />} Display (advanced)
          </CollapsibleToggle>
          {showAdvanced && (
            <>
              <InputGroup
                label="Display Group Name"
                labelInformationTooltip="The heading shown for this value in the read-only summary. Fields that share the exact same Display Group Name are grouped under one heading — put the same name on every field you want grouped together."
                {...register('displayGroupName')}
                errorMessage={errors.displayGroupName?.message}
              />
              <InputGroup
                label="Display Group Description"
                labelInformationTooltip="Optional description shown under the display group's label."
                {...register('displayGroupDescription')}
                errorMessage={errors.displayGroupDescription?.message}
              />
              <InputGroup
                label="Group Index"
                inputMode="numeric"
                labelInformationTooltip="Which line, within this Display Group Name, this field's value appears on. To make two fields appear on the SAME line (e.g. an amount and its unit side by side, like '$100.00 per week'), give them the same Display Group Name AND the same Group Index — then use Group Sub-Index to order them within that line. A different Group Index starts a new line under the same heading."
                {...register('displayGroupIndex')}
                errorMessage={errors.displayGroupIndex?.message}
              />
              <InputGroup
                label="Group Sub-Index"
                inputMode="numeric"
                labelInformationTooltip="Left-to-right order of this value among the other fields sharing the same Display Group Name and Group Index (i.e. the same line)."
                {...register('displayGroupSubIndex')}
                errorMessage={errors.displayGroupSubIndex?.message}
              />
              <InputGroup
                label="Display Prefix"
                labelInformationTooltip="Text shown immediately before the value (e.g. $)."
                {...register('displayPrefix')}
                errorMessage={errors.displayPrefix?.message}
              />
              <InputGroup
                label="Display Suffix"
                labelInformationTooltip="Text shown immediately after the value (e.g. per week)."
                {...register('displaySuffix')}
                errorMessage={errors.displaySuffix?.message}
              />
              <InputGroup
                label="Display Order"
                inputMode="numeric"
                labelInformationTooltip="Order of this field in the read-only summary view."
                {...register('order')}
                errorMessage={errors.order?.message}
              />
              <InputGroup
                label="Edit Description"
                labelInformationTooltip="Helper text shown under the field in the form."
                {...register('editDisplayDescription')}
                errorMessage={errors.editDisplayDescription?.message}
              />
            </>
          )}
        </FieldStack>
      )}
    </Drawer>
  )
}
